import React, { useEffect, useState } from 'react';
import { Card, Button, Alert, Icon, Checkbox, HTMLSelect, Tooltip } from '@blueprintjs/core';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import { IconNames } from '@blueprintjs/icons';
import { deleteSimulation, getDefaultSortOrder, getSimulationsPageNumber, type Simulation, type SimulationPage, type SimulationSort } from '../api/cppmodelApi';

const SORT_OPTIONS: { value: SimulationSort; label: string }[] = [
    { value: 'name', label: 'Name' },
    { value: 'lastExecution', label: 'Last run' },
    { value: 'executions', label: 'Runs' },
];

const SORT_ORDER_LABELS: Record<SimulationSort, { asc: string; desc: string }> = {
    name: { asc: 'A to Z', desc: 'Z to A' },
    lastExecution: { asc: 'Oldest first', desc: 'Newest first' },
    executions: { asc: 'Fewest first', desc: 'Most first' },
};

// "3 runs · last 2026-10-06 17:45 UTC"
const describeRuns = (simulation: Simulation) => [
    `${simulation.totalExecutions} ${simulation.totalExecutions === 1 ? 'run' : 'runs'}`,
    simulation.lastExecutionAt ? `last ${simulation.lastExecutionAt.slice(0, 16)} UTC` : '',
].filter(Boolean).join(' · ');


export const SimulationList: React.FC<{ scope: string }> = ({ scope = 'user' }) => {
    const navigate = useNavigate();
    const simulationPage = useLoaderData() as SimulationPage | null;
    const simulations = simulationPage?.items ?? null;
    const [searchParams, setSearchParams] = useSearchParams();
    const page = getSimulationsPageNumber(searchParams);
    const pageCount = simulationPage ? Math.max(Math.ceil(simulationPage.total / simulationPage.limit), 1) : 1;

    // const [simulations, setSimulations] = useState<Simulation[]>([
    //     { id: 1, name: 'Simulation A' },
    //     { id: 2, name: 'Simulation B' },
    //     { id: 3, name: 'Simulation C' },
    // ]);

    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const [simulationsToDelete, setSimulationsToDelete] = useState<Simulation[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const sort = simulationPage?.sort ?? 'name';
    const order = simulationPage?.order ?? 'asc';

    const goToPage = (nextPage: number) => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (nextPage > 1) {
                next.set('page', `${nextPage}`);
            } else {
                next.delete('page');
            }
            return next;
        });
        setSelectedIds(new Set());
    };

    // A new order starts again from the first page. The direction is only in
    // the URL when it differs from the server's default for that field.
    const changeSort = (nextSort: SimulationSort, nextOrder = getDefaultSortOrder(nextSort)) => {
        const next = new URLSearchParams();
        if (nextSort !== 'name' || nextOrder !== 'asc') {
            next.set('sort', nextSort);
        }
        if (nextOrder !== getDefaultSortOrder(nextSort)) {
            next.set('order', nextOrder);
        }
        setSearchParams(next);
        setSelectedIds(new Set());
    };

    // Past the last page (e.g. after deleting its only simulations): show the last one instead
    useEffect(() => {
        if (simulationPage && page > pageCount) {
            setSearchParams((current) => {
                const next = new URLSearchParams(current);
                if (pageCount > 1) {
                    next.set('page', `${pageCount}`);
                } else {
                    next.delete('page');
                }
                return next;
            }, { replace: true });
        }
    }, [simulationPage, page, pageCount, setSearchParams]);

    const handleVisitSimulation = (simulation: Simulation) => {
        navigate(`/results/${simulation.id}`, { state: { simulationName: simulation.name } });
    };

    const toggleSelected = (simulation: Simulation) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (!next.delete(simulation.id)) {
                next.add(simulation.id);
            }
            return next;
        });
    };

    const stopSelecting = () => {
        setIsSelecting(false);
        setSelectedIds(new Set());
    };

    const handleDeleteClick = (simulation: Simulation) => {
        setSimulationsToDelete([simulation]);
        setIsDeleteAlertOpen(true);
    };

    const handleDeleteSelectedClick = () => {
        setSimulationsToDelete((simulations ?? []).filter(simulation => selectedIds.has(simulation.id)));
        setIsDeleteAlertOpen(true);
    };

    const confirmDelete = async () => {
        if (simulationsToDelete.length === 0) {
            return;
        }
        setIsDeleting(true);
        const results = await Promise.all(simulationsToDelete.map(simulation => deleteSimulation(simulation.id)));
        const failed = simulationsToDelete.filter((_, i) => !results[i]);
        if (failed.length > 0) {
            console.error('Failed to delete simulations:', failed.map(simulation => simulation.name));
        }
        if (failed.length < simulationsToDelete.length) {
            // Refresh the list to drop the deleted simulations
            window.location.reload();
        } else {
            setIsDeleting(false);
            setIsDeleteAlertOpen(false);
        }
    };

    const cancelDelete = () => {
        setIsDeleteAlertOpen(false);
        setSimulationsToDelete([]);
    };

    return (
        <div className="cm-page">
            <div className="cm-page-header">
                <div>
                    <h1 className="cm-page-title">{scope === 'team' ? 'Team Simulations' : 'My Simulations'}</h1>
                    <p className="cm-page-subtitle">
                        {scope === 'team'
                            ? "All simulations in your team's workspace."
                            : 'Simulations which you were the last one running.'}
                    </p>
                </div>
                {simulations && simulations.length > 0 && (
                    <div className="cm-page-header-actions">
                        <div className="cm-sort">
                            <HTMLSelect
                                aria-label="Sort by"
                                value={sort}
                                options={SORT_OPTIONS.map(option => ({ ...option, label: `Sort: ${option.label}` }))}
                                onChange={(event) => changeSort(event.currentTarget.value as SimulationSort)}
                            />
                            <Tooltip content={SORT_ORDER_LABELS[sort][order]}>
                                <Button
                                    icon={order === 'asc' ? 'sort-asc' : 'sort-desc'}
                                    aria-label={`${SORT_ORDER_LABELS[sort][order]}; click for ${SORT_ORDER_LABELS[sort][order === 'asc' ? 'desc' : 'asc'].toLowerCase()}`}
                                    onClick={() => changeSort(sort, order === 'asc' ? 'desc' : 'asc')}
                                />
                            </Tooltip>
                        </div>
                        {(scope === 'user') && isSelecting && selectedIds.size > 0 && (
                            <Button icon="trash" intent="danger" onClick={handleDeleteSelectedClick}>
                                Delete selected ({selectedIds.size})
                            </Button>
                        )}
                        {(scope === 'user') && (isSelecting ? (
                            <Button icon="cross" onClick={stopSelecting}>Cancel</Button>
                        ) : (
                            <Button icon="tick" onClick={() => setIsSelecting(true)}>Select</Button>
                        ))}
                    </div>
                )}
            </div>
            <div className="cm-sim-list">
                {(simulations) &&
                    simulations.map(simulation => (
                        <div key={simulation.id} className="cm-sim-row">
                            {(scope === 'user') && isSelecting && (
                                <Checkbox
                                    className="cm-sim-card-select"
                                    checked={selectedIds.has(simulation.id)}
                                    onChange={() => toggleSelected(simulation)}
                                    aria-label={`Select ${simulation.name}`}
                                />
                            )}
                            <Card interactive={true} className="cm-sim-card" onClick={() => handleVisitSimulation(simulation)}>
                                <div className="cm-sim-card-title">
                                    <Icon icon={IconNames.TimelineLineChart} size={20} />
                                    <div style={{ minWidth: 0 }}>
                                        <h2 className="cm-sim-card-name">{simulation.name}</h2>
                                        {simulation.owner && <span className="cm-sim-card-owner">{simulation.owner}</span>}
                                        <span className="cm-sim-card-runs">{describeRuns(simulation)}</span>
                                    </div>
                                </div>
                                <div className="cm-sim-card-actions" onClick={(e) => e.stopPropagation()}>
                                    <Button icon="eye-open" onClick={() => handleVisitSimulation(simulation)}>
                                        Visit Simulation
                                    </Button>
                                    {(scope === 'user') && (
                                        <Button icon="trash" intent="danger" onClick={() => handleDeleteClick(simulation)}>
                                            Delete
                                        </Button>
                                    )}
                                </div>
                                {/* <div style={{ height: '150px', backgroundColor: '#f5f5f5', marginTop: '10px', borderRadius: '5px' }}>
                                <Icon icon="timeline-line-chart" iconSize={50} />
                                <span style={{ marginLeft: '10px' }}>Simulation Graph</span>
                            </div> */}
                            </Card>
                        </div>
                    ))}
                {(!simulations || simulations.length === 0) && page === 1 && (
                    <div className="cm-empty">No simulations yet. Use <strong>New</strong> to create one.</div>
                )}
            </div>
            {simulationPage && pageCount > 1 && (
                <div className="cm-pager">
                    <Button icon="chevron-left" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                        Previous
                    </Button>
                    <span className="cm-pager-status">
                        {simulationPage.offset + 1}–{simulationPage.offset + simulationPage.items.length} of {simulationPage.total} · page {page} of {pageCount}
                    </span>
                    <Button rightIcon="chevron-right" disabled={page >= pageCount} onClick={() => goToPage(page + 1)}>
                        Next
                    </Button>
                </div>
            )}

            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
                intent="danger"
                isOpen={isDeleteAlertOpen}
                loading={isDeleting}
                onCancel={cancelDelete}
                onConfirm={confirmDelete}
            >
                {simulationsToDelete.length === 1 ? (
                    <p>
                        Are you sure you want to delete the simulation <strong>{simulationsToDelete[0].name}</strong>?
                    </p>
                ) : (
                    <>
                        <p>Are you sure you want to delete these {simulationsToDelete.length} simulations?</p>
                        <ul>
                            {simulationsToDelete.map(simulation => (
                                <li key={simulation.id}><strong>{simulation.name}</strong></li>
                            ))}
                        </ul>
                    </>
                )}
            </Alert>
        </div>
    );
};
